import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { initialPath } from "~misc/paths";
import ReportFilterBar from "../../components/ReportFilterBar";
import ReportListings from "../../components/ReportListings";
import { reportListingTypes } from "../../components/reportListingTypes";
import StatsCard from "../../components/StatsCard";
import { reportSectionIds } from "../../components/StatsCard.data";
import { useCustomerCreditData } from "../../hooks/useCustomerCreditData";

const CustomerCreditPage = () => {
  const { clientes, clientesTodos, statsSections, listingData, listingDataTodos, updatedAt, isLoading, isRefreshing } = useCustomerCreditData();

  const [searchValue, setSearchValue] = useState("");
  const [orderValue, setOrderValue] = useState("maior-credito");


  const { aba } = useParams();
  const navigate = useNavigate();
  const activeActionKey = aba === "todos" ? "todos" : "rota";

  const setActiveActionKey = useCallback(
    (key, { replace = false } = {}) => {
      const base = `${initialPath}/gestao-de-vendas/credito-de-clientes`;
      navigate(key === "todos" ? `${base}/todos` : base, { replace });
    },
    [navigate]
  );

  const autoSwitchedRef = useRef(false);

  const isRotaEmpty   = (clientes?.length ?? 0) === 0;
  const hasTodosRows  = (clientesTodos?.length ?? 0) > 0;

  useEffect(() => {
    if (autoSwitchedRef.current) return;
    if (activeActionKey === "rota" && isRotaEmpty && hasTodosRows) {
      autoSwitchedRef.current = true;
      setActiveActionKey("todos", { replace: true });
    }
  }, [activeActionKey, isRotaEmpty, hasTodosRows, setActiveActionKey]);

  const showRotaEmptyNotice =
    activeActionKey === "rota" && isRotaEmpty && hasTodosRows;

  const sourceListingData = activeActionKey === "todos" ? listingDataTodos : listingData;

  const orderOptions = useMemo(
    () => [
      { value: "maior-credito", label: "Ordem Decrescente" },
      { value: "alfabetica",    label: "Ordem Alfabética" },
    ],
    [],
  );

  const filteredListingData = useMemo(() => {
    const source = sourceListingData?.[reportListingTypes.creditoClientes];
    if (!source) return sourceListingData;

    let rows = source.rows;

    if (searchValue.trim()) {
      const q = searchValue.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          r.cliente?.toLowerCase().includes(q) ||
          r.grupoCentralizador?.toLowerCase().includes(q),
      );
    }

    if (orderValue === "maior-credito") {
      rows = [...rows].sort((a, b) => b._creditoTotalRaw - a._creditoTotalRaw);
    } else {
      rows = [...rows].sort((a, b) => {
        const nomeA = String(a.cliente || "").split(" - ").slice(1).join(" - ").trim();
        const nomeB = String(b.cliente || "").split(" - ").slice(1).join(" - ").trim();
        return nomeA.localeCompare(nomeB, "pt-BR", { sensitivity: "base" });
      });
    }

    return {
      ...sourceListingData,
      [reportListingTypes.creditoClientes]: { ...source, rows },
    };
  }, [sourceListingData, searchValue, orderValue]);

  const clearFilters = () => {
    setSearchValue("");
    setOrderValue("maior-credito");
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <span className="text-[#1A2B49] font-semibold">Carregando...</span>
      </div>
    );
  }

  return (
    <div className="pb-10">
      {isRefreshing && (
        <div className="container">
          <div className="w-[130px] flex mt-4 items-center gap-2 bg-[#01054C] rounded-md px-4 py-2 text-xs font-semibold text-[#fff] animate-pulse">
            <span className="rounded-full" />
            Atualizando...
          </div>
        </div>
      )}
      <StatsCard
        sections={statsSections}
        sectionId={reportSectionIds.creditoClientes}
        activeActionKey={activeActionKey}
        onActionChange={(_sectionId, actionKey) => {
          setActiveActionKey(actionKey);
          setSearchValue("");
        }}
      />

      {showRotaEmptyNotice && (
        <div className="container">
          <div className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-[#1A2B49] md:flex-row md:items-center md:justify-between">
            <span>Não há dados para os clientes da rota de hoje.</span>
            <button
              type="button"
              onClick={() => setActiveActionKey("todos")}
              className="self-start rounded-md border border-[#0655A3] bg-white px-3 py-1.5 text-xs font-semibold text-[#0655A3] hover:bg-[#0655A3] hover:text-white md:self-auto"
            >
              Ver todos os clientes
            </button>
          </div>
        </div>
      )}

      <div className="container py-2 mb-2">
        <ReportFilterBar
          searchValue={searchValue}
          searchPlaceholder="Busque por Código de Cliente ou Razão Social"
          onSearchValueChange={setSearchValue}
          onSearchSubmit={setSearchValue}
          onClearFilters={clearFilters}
          showClearButton
          orderValue={orderValue}
          orderOptions={orderOptions}
          onOrderChange={setOrderValue}
          orderPlacement="bottom"
        />
      </div>

      <ReportListings
        type={reportListingTypes.creditoClientes}
        dataByType={filteredListingData}
        updatedAt={updatedAt}
        footerNote="Atualização de segunda a sábado de hora em hora das 08:00 às 19:00"
      />
    </div>
  );
};

export default CustomerCreditPage;
